/* eslint-disable @hakio/no-untranslated-terminology -- flag panel copy is internal (staff-only), not tenant-facing */
import { CloudArrowDown } from "@phosphor-icons/react"
import { useState } from "react"

import { Button, InlineAlert } from "~/components/design-system/components"
import {
  DialogBodyV2,
  DialogContentV2,
  DialogFooterV2,
  DialogHeaderV2,
  DialogRoot,
  DialogTitle,
} from "~/components/reusable/Dialog"
import { useFlagPanel } from "~/features/feature-flags/client/FlagPanelProvider"
import { useInvalidateFlagPanel } from "~/features/feature-flags/client/useFlagPanelData"
import type { FlagChangeRequest } from "~/features/feature-flags/shared/contracts"
import { FEATURE_FLAG_KEYS, flagName } from "~/features/feature-flags/shared/registry"
import { api } from "~/utils/api"

function shown(value: boolean | null) {
  if (value === null) return "none"
  return value ? "on" : "off"
}

const COMPANY_GROUPS = [
  ["On", true],
  ["Off", false],
  ["Override removed", null],
] as const

/** The planned changes per flag: the default override, then the companies set on, off or back to
 * the default. */
function ChangeSummary({ changes }: { changes: readonly FlagChangeRequest[] }) {
  const panel = useFlagPanel()
  const byFlag = Map.groupBy(changes, (c) => c.flagKey)
  return (
    <ul className="flex max-h-80 flex-col gap-3 overflow-y-auto">
      {FEATURE_FLAG_KEYS.flatMap((flagKey) => {
        const own = byFlag.get(flagKey)
        if (!own) return []
        const byDefault = own.find((c) => c.scope === "default")
        return (
          <li key={flagKey} className="flex flex-col gap-1">
            <span className="label-base text-fg-base">{flagName(flagKey)}</span>
            {byDefault && (
              <span className="paragraph-sm text-fg-subtle">
                Default override: {shown(byDefault.expected)} → {shown(byDefault.value)}
              </span>
            )}
            {COMPANY_GROUPS.map(([label, value]) => {
              const names = own
                .filter((c) => c.scope === "org" && c.value === value)
                .map((c) => panel.companyName(c.targetId))
                .sort()
              return (
                names.length > 0 && (
                  <span key={label} className="paragraph-sm text-fg-subtle">
                    {label} ({names.length}): {names.join(", ")}
                  </span>
                )
              )
            })}
          </li>
        )
      })}
    </ul>
  )
}

/** Reads LaunchDarkly's targeting for every company when opened, shows what a sync would change,
 * and applies it on Sync. */
export function LaunchDarklyImportButton() {
  const panel = useFlagPanel()
  const [open, setOpen] = useState(false)
  const preview = api.featureFlagsPanel.launchDarklyImport.useQuery(undefined, {
    enabled: open,
    staleTime: 0,
    retry: false,
  })
  const invalidatePanel = useInvalidateFlagPanel()
  const sync = api.featureFlagsPanel.syncLaunchDarkly.useMutation({
    onSuccess: () => Promise.all([preview.refetch(), invalidatePanel()]),
  })
  const data = preview.data

  return (
    <>
      <Button
        variant="neutral"
        size="sm"
        requires="staff"
        wrapperClassName="ml-auto"
        onClick={() => setOpen(true)}
      >
        <CloudArrowDown />
        Compare with LaunchDarkly
      </Button>
      <DialogRoot open={open} onOpenChange={setOpen}>
        <DialogContentV2 className="max-w-2xl">
          <DialogHeaderV2>
            <DialogTitle>Compare with LaunchDarkly</DialogTitle>
          </DialogHeaderV2>
          <DialogBodyV2>
            {preview.isFetching && !data && (
              <p className="paragraph-sm text-fg-subtle">Reading LaunchDarkly for every company</p>
            )}
            {preview.error && (
              <InlineAlert tone="destructive" description={preview.error.message} />
            )}
            {data === null && (
              <InlineAlert
                tone="info"
                description="A local server doesn't read LaunchDarkly, so there is nothing to compare or sync."
              />
            )}
            {data && (
              <>
                <p className="label-base text-fg-base">
                  {data.changes.length === 0
                    ? "0 changes: in sync"
                    : `${data.changes.length} changes`}
                  , {data.mismatches.length} mismatches for {data.companyCount} companies
                </p>
                <ChangeSummary changes={data.changes} />
                {data.warnings.length > 0 && (
                  <InlineAlert
                    tone="warning"
                    size="block"
                    title="Warnings"
                    description={
                      <ul className="flex flex-col gap-1">
                        {data.warnings.map((warning) => (
                          <li key={warning}>{warning}</li>
                        ))}
                      </ul>
                    }
                  />
                )}
                {data.mismatches.length > 0 && (
                  <InlineAlert
                    tone="warning"
                    size="block"
                    title="Mismatches after the sync"
                    description={
                      <ul className="flex max-h-60 flex-col gap-1 overflow-y-auto">
                        {data.mismatches.map(({ flagKey, orgId, launchDarkly, blockedBy }) => (
                          <li key={`${flagKey}-${orgId}`}>
                            {flagName(flagKey)} for {panel.companyName(orgId)}:{" "}
                            {shown(launchDarkly)} in LaunchDarkly, {shown(!launchDarkly)} in-house
                            {blockedBy && ` (needs ${flagName(blockedBy)})`}
                          </li>
                        ))}
                      </ul>
                    }
                  />
                )}
                <InlineAlert
                  tone="info"
                  description={
                    data.switchOn
                      ? "The hakioFeatureFlags switch is on, so the panel owns the flags and the sync has stopped."
                      : "Sync copies these into the in-house flags. Do it once after the first deploy, and again before turning the switch on or archiving a flag in LaunchDarkly. Only company targeting is synced. User targets and user-attribute rules in LaunchDarkly are not: set user overrides in the panel."
                  }
                />
              </>
            )}
          </DialogBodyV2>
          <DialogFooterV2 className="justify-end gap-2">
            {sync.error && <InlineAlert tone="destructive" description={sync.error.message} />}
            <Button variant="neutral" size="sm" onClick={() => setOpen(false)}>
              Close
            </Button>
            <Button
              size="sm"
              requires="staff"
              disabled={!data || data.switchOn || data.changes.length === 0 || preview.isFetching}
              loading={sync.isPending}
              onClick={() => sync.mutate()}
            >
              Sync {data?.changes.length ?? 0} changes
            </Button>
          </DialogFooterV2>
        </DialogContentV2>
      </DialogRoot>
    </>
  )
}
